import {useHistory} from "react-router";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {selectedSiteSelector} from "../../authentication/redux";
import React, {useCallback, useMemo, useState} from "react";
import {ServerPage, Supervisor} from "../../app/types";
import {Sort, useInfiniteScroll} from "../../common/components/table/useInfiniteScroll";
import {api, buildPaginatedUrl} from "../../common/api";
import {ColumnDefinition, InfiniteScrollTable} from "../../common/components/table/Table";
import {Link} from "@mui/material";
import TableRowActionsMenu from "../../common/components/table/TableRowActionsMenu";
import {sorterHeader} from "../../common/components/table/SorterHeader";
import {SearchField} from "../../common/components/SearchField";
import ErrorAlert from "../../common/components/ErrorAlert";
import DeletionModal from "../../common/components/DeletionModal";
import {RouteDefinition} from "../../app/App";
import {TitledBody} from "../../common/components/TitledBody";
import {AddSupervisor} from "./addSupervisor";
import {AddButton} from "../../common/components/AddButton";
import {DetailSupervisor} from "./detail";
import {useAccessHelpers} from "../../common/util/hooks";

export const detailSupervisorUrl = '/services/supervisor/:id'
export const addSupervisorUrl = '/services/supervisor/new'

export const routes: RouteDefinition[] = [
    {
        href: addSupervisorUrl,
        component: AddSupervisor,
        id: 'supervisor-add',
        parentId: 'business-reporting-overview',
        translationKey: 'supervisors.add.title',
    },
    {
        href: detailSupervisorUrl,
        component: DetailSupervisor,
        id: 'supervisor-detail',
        parentId: 'business-reporting-overview',
    }
].map(routeDefinition => ({ ...routeDefinition, menuId: 'business-reporting-overview' }))

export const SupervisorList = () => {
    const history = useHistory()
    const { t } = useTranslation()
    const site = useSelector(selectedSiteSelector)
    const [supervisorToDelete, setSupervisorToDelete] = useState<Supervisor | null>(null)
    const {hasWriteAccess, onlyWithWriteAccess} = useAccessHelpers()

    const fetch = useCallback(
        async (pageParam = 0, sortParam: Sort, searchTermParam: string | null): Promise<ServerPage<Supervisor>> => {
            if (!site) return { content: [], totalPages: 0, number: 0, totalElements: 0 }
            const { data } = await api.get(buildPaginatedUrl(`/api/site/${site.productId}/business-reporting/supervisors`, pageParam, sortParam, searchTermParam))
            return data
        },
        [site]
    )

    const { sort, setSort, setSearchTerm, rows, error, fetchNextPage, hasNextPage, isFetching, refetch } = useInfiniteScroll<Supervisor>({
        queryKey: `supervisors-${site?.productId}`,
        initialSort: { field: 'lastName', order: 'asc' },
        fetchFunction: fetch,
    })

    const rowActions = useMemo(
        () => [
            {
                key: 'edit',
                text: t('general.edit'),
                onClick: (record: Supervisor) => {
                    history.push(detailSupervisorUrl.replace(':id', record.id || '') + '?edit=true#0')
                },
                dividerBeneath: true,
                needsWriteAccess: true
            },
            {
                key: 'delete',
                text: t('general.delete'),
                onClick: (record: Supervisor) => {
                    setSupervisorToDelete(record)
                },
                needsWriteAccess: true
            },
        ],
        [t, history]
    )

    const columns: ColumnDefinition<Supervisor>[] = useMemo(
        () => [
            {
                title: t('call-pickup-groups.table.name'),
                key: 'userLastName',
                sorter: true,
                render: (text: string, record: Supervisor) => <Link href={detailSupervisorUrl.replace(':id', record.id || '')}>{`${record.userLastName} ${record.userFirstName}`}</Link>,
            },
            {
                title: t('supervisors.supervised-sites'),
                key: 'userFirstName',
                sorter: false,
                render: (text: string, record: Supervisor) =>
                    <>
                        {record.superviseAllSites ? t('supervisors.all-sites') :
                            (record.supervisedSites == null || record.supervisedSites.length === 0) ?
                                t('general.none') : record.supervisedSites.map(ss => ss.siteName).join(', ')}
                    </>,
            },
            {
                title: '',
                key: 'id',
                sorter: false,
                render: (text: string, record: Supervisor) => <TableRowActionsMenu record={record} menuItems={rowActions} readOnly={!hasWriteAccess} />,
                style: { width: '25%' },
            },
        ],
        [t, rowActions, hasWriteAccess]
    )

    const onSearch = useCallback(async (searchTerm: string) => setSearchTerm(searchTerm), [setSearchTerm])

    const sorter = useCallback(col => sorterHeader(col, sort, setSort), [sort, setSort])

    const getRowKey = useCallback(supervisor => supervisor.uuid, [])

    const getDeleteUrl = useCallback((e: Supervisor | null) => (site && e ? `/api/site/${site.productId}/business-reporting/supervisors/${e.id}` : null), [site])

    return (
        <TitledBody title={t('supervisors.title')} buttonsPanel={
            onlyWithWriteAccess(<AddButton
                onClick={() => {
                    history.push(addSupervisorUrl)
                    return null
                }}
                label={`${t('supervisors.create-new')}`}
            />)
        }>
            <SearchField onSearch={onSearch} />
            <ErrorAlert showAlert={!!error} />
            <InfiniteScrollTable<Supervisor>
                hasNextPage={hasNextPage}
                data={rows}
                fetchNextPage={fetchNextPage}
                isFetching={isFetching}
                getRowKey={getRowKey}
                sorter={sorter}
                columns={columns}
                tableId={'supervisorsTable'}
            />
            <DeletionModal<Supervisor | null>
                nameToDelete={supervisorToDelete => `${supervisorToDelete?.userLastName} ${supervisorToDelete?.userFirstName}`}
                itemToDelete={supervisorToDelete} deleteUrl={getDeleteUrl} onClose={() => setSupervisorToDelete(null)} onSuccess={refetch} />
        </TitledBody>
    )
}